/**
 * Secure Password Manager
 *
 * SECURITY: Stores encrypted password in sessionStorage to persist across page refreshes
 * Uses Web Crypto API for encryption. SessionStorage is cleared when browser/tab closes.
 */

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Auth - securePasswordManager');

interface PasswordCache {
  password: string;
  expiresAt: number;
  userId: string;
}

interface StoredSession {
  encryptedPassword: string;
  iv: string;
  expiresAt: number;
  userId: string;
}

// In-memory password cache (cleared on page refresh)
let cachedPassword: PasswordCache | null = null;

// Default session duration: 1 hour
const DEFAULT_SESSION_DURATION_MS = 60 * 60 * 1000;
// "Never expire" duration: 10 years
const NEVER_MS = 10 * 365 * 24 * 60 * 60 * 1000;
// Idle timeout: 30 minutes of inactivity
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

// SessionStorage key
const SESSION_KEY = 'encryption_session';

// Activity tracking
let lastActivityTime = Date.now();
let idleTimeoutId: NodeJS.Timeout | null = null;
let activityListenersAttached = false;

/**
 * Derive encryption key for password storage
 */
async function getDerivedKey() {
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode('lovable_encryption_session_key_v1'),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('lovable_session_salt_v1'),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypt password for sessionStorage
 */
async function encryptPassword(
  password: string,
): Promise<{ encrypted: string; iv: string }> {
  const key = await getDerivedKey();
  const encoder = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(password),
  );

  return {
    encrypted: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
    iv: btoa(String.fromCharCode(...iv)),
  };
}

/**
 * Decrypt password from sessionStorage
 */
async function decryptPassword(
  encrypted: string,
  ivStr: string,
): Promise<string> {
  const key = await getDerivedKey();
  const encryptedData = new Uint8Array(
    atob(encrypted)
      .split('')
      .map((c) => c.charCodeAt(0)),
  );
  const iv = new Uint8Array(
    atob(ivStr)
      .split('')
      .map((c) => c.charCodeAt(0)),
  );

  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encryptedData,
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

/**
 * Store session in sessionStorage
 */
async function storeSession(
  userId: string,
  password: string,
  durationMs: number,
): Promise<void> {
  try {
    const { encrypted, iv } = await encryptPassword(password);
    const expiresAt = Date.now() + (durationMs === 0 ? NEVER_MS : durationMs);
    const session: StoredSession = {
      encryptedPassword: encrypted,
      iv,
      expiresAt,
      userId,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    logger.error('Failed to store encryption session:', error as Error);
  }
}

/**
 * Restore session from sessionStorage
 */
async function restoreSession(): Promise<PasswordCache | null> {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }

    const session = parsed as Partial<StoredSession>;
    if (
      typeof session.expiresAt !== 'number' ||
      typeof session.encryptedPassword !== 'string' ||
      typeof session.iv !== 'string' ||
      typeof session.userId !== 'string'
    ) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }

    // Check if expired
    if (Date.now() > session.expiresAt) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }

    // Decrypt password
    const password = await decryptPassword(
      session.encryptedPassword,
      session.iv,
    );

    return {
      password,
      userId: session.userId,
      expiresAt: session.expiresAt,
    };
  } catch (error) {
    logger.error('Failed to restore encryption session:', error as Error);
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

/**
 * Clear stored session
 */
function clearStoredSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Handle user activity - reset idle timer
 */
function handleUserActivity(): void {
  lastActivityTime = Date.now();

  // Reset idle timeout
  if (idleTimeoutId) {
    clearTimeout(idleTimeoutId);
  }

  // Set new timeout to clear password after inactivity
  idleTimeoutId = setTimeout(() => {
    const inactiveTime = Date.now() - lastActivityTime;
    if (inactiveTime >= IDLE_TIMEOUT_MS && cachedPassword) {
      logger.info(
        '[Security] Clearing cached password due to 30min inactivity',
        {},
        'auth',
      );

      clearPasswordCache();
    }
  }, IDLE_TIMEOUT_MS);
}

/**
 * Attach activity listeners to track user interaction
 */
function attachActivityListeners(): void {
  if (activityListenersAttached || typeof window === 'undefined') {
    return;
  }

  const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

  // Throttle activity tracking (don't reset timer on every single event)
  let throttleTimer: NodeJS.Timeout | null = null;
  const throttledHandler = () => {
    if (!throttleTimer) {
      throttleTimer = setTimeout(() => {
        handleUserActivity();
        throttleTimer = null;
      }, 1000); // Check activity max once per second
    }
  };

  events.forEach((event) => {
    window.addEventListener(event, throttledHandler, { passive: true });
  });

  activityListenersAttached = true;

  // Initialize first activity
  handleUserActivity();
}

/**
 * Detach activity listeners
 */
function detachActivityListeners(): void {
  if (!activityListenersAttached || typeof window === 'undefined') {
    return;
  }

  // Clear timeout
  if (idleTimeoutId) {
    clearTimeout(idleTimeoutId);
    idleTimeoutId = null;
  }

  activityListenersAttached = false;
}

/**
 * Store encryption password in memory and optionally sessionStorage
 *
 * @param userId - User ID
 * @param password - Encryption password
 * @param durationMs - How long to keep password in memory (default: 1 hour)
 * @param useSessionStorage - Whether to persist in sessionStorage (requires user consent)
 */
export async function cachePassword(
  userId: string,
  password: string,
  durationMs: number = DEFAULT_SESSION_DURATION_MS,
  useSessionStorage: boolean = false,
): Promise<void> {
  const expMs = durationMs === 0 ? NEVER_MS : durationMs;

  // Always cache in memory
  cachedPassword = {
    password,
    userId,
    expiresAt: Date.now() + expMs,
  };

  // Only store in sessionStorage if user has given consent
  if (useSessionStorage) {
    await storeSession(userId, password, expMs);
  } else {
    // Clear sessionStorage if user opted out
    clearStoredSession();
  }

  // SECURITY: Start tracking user activity for idle timeout
  attachActivityListeners();
}

/**
 * Retrieve cached password if available and not expired
 * Attempts to restore from sessionStorage if not in memory
 *
 * @param userId - User ID
 * @returns Password if available, null if expired or not found
 */
export async function getCachedPassword(
  userId: string,
): Promise<string | null> {
  // Check memory cache first
  if (cachedPassword && cachedPassword.userId === userId) {
    if (Date.now() > cachedPassword.expiresAt) {
      clearPasswordCache();
      return null;
    }
    return cachedPassword.password;
  }

  // Try to restore from sessionStorage
  const restored = await restoreSession();
  if (restored && restored.userId === userId) {
    cachedPassword = restored;
    return restored.password;
  }

  return null;
}

/**
 * Clear cached password from memory and sessionStorage
 */
export function clearPasswordCache(): void {
  if (cachedPassword) {
    // Overwrite password with zeros before clearing
    cachedPassword.password = '\0'.repeat(cachedPassword.password.length);
    cachedPassword = null;
  }
  clearStoredSession();

  // Stop tracking activity
  detachActivityListeners();
}

/**
 * Check if password is cached and valid
 *
 * @param userId - User ID
 * @returns True if password is available in memory or sessionStorage
 */
export async function hasValidPasswordCache(userId: string): Promise<boolean> {
  const password = await getCachedPassword(userId);
  return password !== null;
}

/**
 * Get time since last user activity (in milliseconds)
 */
export function getIdleTime(): number {
  return Date.now() - lastActivityTime;
}

/**
 * Check if user has been idle for too long
 */
export function isIdle(): boolean {
  return getIdleTime() >= IDLE_TIMEOUT_MS;
}

/**
 * Extend the expiration time of cached password
 * Call this on user activity to keep session alive
 *
 * @param userId - User ID
 * @param additionalMs - Additional time to extend (default: 1 hour)
 * @param updateStorage - Whether to update sessionStorage (default: true)
 */
export async function extendPasswordCache(
  userId: string,
  additionalMs: number = DEFAULT_SESSION_DURATION_MS,
  updateStorage: boolean = true,
): Promise<boolean> {
  if (!cachedPassword || cachedPassword.userId !== userId) {
    return false;
  }

  const expMs = additionalMs === 0 ? NEVER_MS : additionalMs;
  cachedPassword.expiresAt = Date.now() + expMs;

  // Only update sessionStorage if it was originally used and user wants to
  if (updateStorage) {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      await storeSession(userId, cachedPassword.password, expMs);
    }
  }

  return true;
}
